import styled from "@emotion/styled";
import { Switch, SwitchProps, TextField, Tooltip } from "@mui/material";
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { useCallback } from "react";
import TimePicker from "./time-picker";

export const InputContainer = styled.div`
    border: 1px solid lightgrey;
    border-radius: 4px;
    padding: 12px 16px 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    vertical-align: bottom;
    margin-bottom: 16px;
`

const ColumnContainer = styled(InputContainer)`
    flex-direction: column;
    align-items: center;
`

const NumberInput = styled(TextField)({
    width: '3ch'
})

const IOSSwitch = styled((props: SwitchProps) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }: any) => ({
    width: 42,
    height: 26,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: 2,
        transitionDuration: '300ms',
        '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
                backgroundColor: theme.palette.primary.main,
                opacity: 1,
                border: 0,
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.5,
            },
        },
        '&.Mui-focusVisible .MuiSwitch-thumb': {
            color: theme.palette.primary.main,
            border: '6px solid #fff',
        },
        '&.Mui-disabled .MuiSwitch-thumb': {
            color:
                theme.palette.mode === 'light'
                    ? theme.palette.grey[100]
                    : theme.palette.grey[600],
        },
        '&.Mui-disabled + .MuiSwitch-track': {
            opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
        },
    },
    '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: 22,
        height: 22,
    },
    '& .MuiSwitch-track': {
        borderRadius: 26 / 2,
        backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
        opacity: 1,
        transition: theme.transitions.create(['background-color'], {
            duration: 500,
        }),
    },
}));

interface Props {
    is_keep_in_touch_email: string;
    is_unsubscribe: string;
    send_emails: boolean;
    send_notifications: boolean;
    numberOfRandomFacts: string;
    numberOfLocationFacts: string;
    notifTimePreferred: string;
    handleChange: (field: any) => (name: string) => unknown;
}

const UserNotificationsSection = ({
    is_keep_in_touch_email,
    is_unsubscribe,
    send_emails,
    send_notifications,
    numberOfRandomFacts,
    numberOfLocationFacts,
    notifTimePreferred,
    handleChange
}: Props) => {
    const handleSwitchChange = useCallback((field: string) => (e: any) => {
        const nextValue = e.target.checked ? "1" : '0';

        handleChange(field)(nextValue);
    }, [handleChange]);

    const handleNegativeSwitchChange = useCallback((field: string) => (e: any) => {
        const nextValue = e.target.checked ? '0' : '1';

        handleChange(field)(nextValue);
    }, [handleChange]);

    const handleBooleanSwitchChange = useCallback((field: string) => (e: any) => {
        const nextValue = e.target.checked;

        handleChange(field)(nextValue);
    }, [handleChange]);

    const handleStringInputChange = useCallback((field: string) => (e: any) => {
        const nextValue = e.target.value;

        handleChange(field)(nextValue);
    }, [handleChange]);


    return (
        <section id="user-notifications" className="flex flex-col">
            <div className="flex gap-2 flex-row mb-0">
                <h4>Notifications</h4>
                <Tooltip title={<span><b>NetworkNerd</b> is unable to remind you about your contacts unless you allow notifications.</span>}>
                    <WarningAmberOutlinedIcon />
                </Tooltip>
            </div>
            <InputContainer>
                Keep in Touch
                <IOSSwitch
                    checked={is_keep_in_touch_email === '1'}
                    onChange={handleSwitchChange('is_keep_in_touch_email')}
                />
            </InputContainer>
            <InputContainer>
                Fact Reminders
                <IOSSwitch
                    checked={is_unsubscribe === '0'}
                    onChange={handleNegativeSwitchChange('is_unsubscribe')}
                />
            </InputContainer>
            <InputContainer>
                Mobile Notifications
                <IOSSwitch
                    checked={send_notifications}
                    onChange={handleBooleanSwitchChange('send_notifications')}
                />
            </InputContainer>
            <InputContainer>
                Email Notifications
                <IOSSwitch
                    checked={send_emails}
                    onChange={handleBooleanSwitchChange('send_emails')}
                />
            </InputContainer>
            <h4 className="mt-0">
                Reminders
            </h4>
            <InputContainer>
                Note reminders per message
                <NumberInput
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    variant="standard"
                    value={numberOfRandomFacts}
                    onChange={handleStringInputChange('numberOfRandomFacts')}
                />
            </InputContainer>
            <InputContainer>
                Location reminders per message
                <NumberInput
                    variant="standard"
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    value={numberOfLocationFacts}
                    onChange={handleStringInputChange('numberOfLocationFacts')}
                />
            </InputContainer>
            <ColumnContainer>
                Time of day to receive reminders
                <TimePicker value={notifTimePreferred} onChange={handleStringInputChange('notifTimePreferred')} />
            </ColumnContainer>
        </section>
    );
};

export default UserNotificationsSection;
